import { useCallback, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import Translator from "../../../components/helpers/Translator";
import { axiosPath } from "../../../../routing/helpers/router";
import minions from "../../../../minions/minions";
import useIsMounted from "../../../components/hooks/useIsMounted";
import _ from "lodash";
import { handleDisabled } from "./tools/comsaReportsTools";

const ModuleComsaWasteManagementEdit = (props) => {
  const { reportOptions, report, codes, managerOptions, onEdit, moduleData } =
    props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState(null);
  const dynamicAreaButton = useRef(null);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    }))
  );

  // < EDIT REPORT
  const editReport = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaReports_editReport"),
          minions.forms.toFormData({
            ...values,
            type: "wasteManagement",
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) {
            setShowModal(false);
            onEdit();
          }
        }),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  const handleEditReport = useCallback(() => {
    const editableReport = _.omit(report, [
      "code",
      "quantity",
      "measureUnit",
      "wasteManagement",
      "operation",
      "valuablePercentage",
    ]);
    setReportFormInitialValues({
      ...editableReport,
      month: Number(report.month),
      year: report.year,
      dynamicArea: [
        {
          code: report.code,
          manager: report.managerId,
          quantity: report.quantity,
          measureUnit: Number(report.measureUnit),
          wasteManagement: Number(report.wasteManagement),
          operation: Number(report.operation),
          valuablePercentage: report.valuablePercentage,
        },
      ],
      deletedFiles: [],
      comment: report.comment
    });
    setShowModal(true);
  }, [report]);
  // < EDIT REPORT

  const removeDynamicArea = useCallback((dynamicArea, arrayHelpers, i) => {
    if (dynamicArea.length > 1) arrayHelpers.remove(i);
  }, []);

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        "deletedFiles",
        deletedFiles.filter((s) => s !== salt)
      );
    } else setFieldValue("deletedFiles", [...deletedFiles, salt]);
  };

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    year: Yup.date()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    dynamicArea: Yup.array().of(
      Yup.object().shape({
        code: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        manager: Yup.number().nullable(),
        quantity: Yup.string()
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        measureUnit: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        wasteManagement: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        operation: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        valuablePercentage: Yup.string()
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(<Translator file="user" name="user.required" />),
      })
    ),
  });
  // < FORM SCHEMA

  // < DELETE REPORT
  const removeReport = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_comsaReports_removeReport"), {
          id: report.id,
          type: "wasteManagement",
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        })
        .then((r) => {
          if (isMounted.current) {
            setShowDeleteModal(false);
            onEdit();
          }
        }),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < DELETE REPORT

  return (
    <>
      <button
        type="button"
        className="btn btn-info btn-xs mr-1"
        onClick={handleEditReport}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit" />,
        )}
        disabled={handleDisabled(moduleData, report?.year)}
      >
        <AppRemixIcon icon="edit" />
      </button>
      <button
        type="button"
        className="btn btn-danger btn-xs"
        onClick={() => setShowDeleteModal(true)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="delete" />,
        )}
        disabled={handleDisabled(moduleData, report?.year)}
      >
        <AppRemixIcon icon="delete-bin-6" />
      </button>
      <AppModal
        show={showDeleteModal}
        type="danger"
        isConfirmBtn
        isCancelBtn
        confirmBtnText={<Translator file="module-comsaReports" name="delete" />}
        title={<Translator file="module-comsaReports" name="delete.report" />}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={removeReport}
      >
        <Translator file="module-comsaReports" name="confirm.delete.report" />
      </AppModal>
      <AppModal
        show={showModal}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="report.form" />,
        )}
        size="lg"
        onClose={() => setShowModal(false)}
      >
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          initialValues={reportFormInitialValues}
          validationSchema={validationSchema}
          onSubmit={editReport}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={<Translator file="module-comsaReports" name="year" />}
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={new Date(2021, 0, 1)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator file="module-comsaReports" name="select.a.year" />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors?.year}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppSingleSelect
                      options={months}
                      onChange={(opt) => setFieldValue('month', opt)}
                      title={<Translator file="module-saicaReports" name="month" />}
                      icon={<AppRemixIcon icon="calendar" />}
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.month"
                        />
                      }
                      value={values.month}
                      error={errors?.month}
                    />
                  </div>
                </div>
                <div className="col-sm-12">
                  <FieldArray
                    name="dynamicArea"
                    render={(arrayHelpers) => (
                      <>
                        {values.dynamicArea.map((da, daIndex) => (
                          <div
                            key={daIndex}
                            className="row pt-2 pb-2"
                            style={{ borderBottom: '1px solid #f4f6f9' }}
                          >
                            <div className="col-lg-12">
                              <button
                                className="btn btn-danger btn-xs float-right"
                                onClick={() =>
                                  removeDynamicArea(
                                    values.dynamicArea,
                                    arrayHelpers,
                                    daIndex,
                                  )
                                }
                                disabled={values.dynamicArea.length === 1}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="delete"
                                  />,
                                )}
                              >
                                <AppRemixIcon icon="delete-bin-6" />
                              </button>
                            </div>
                            <div className="col-md-6">
                              <AppSingleSelect
                                icon={<AppRemixIcon icon="barcode" />}
                                options={codes}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="code"
                                  />,
                                )}
                                value={values?.dynamicArea[daIndex]?.code}
                                onChange={(opt) =>
                                  setFieldValue(`dynamicArea[${daIndex}].code`, opt)
                                }
                                isLoading={!codes}
                                hasSearchBox
                                hasSelectOptions
                                error={
                                  errors?.dynamicArea &&
                                  errors?.dynamicArea[daIndex]?.code
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <AppSingleSelect
                                icon={<AppRemixIcon icon="user-settings" />}
                                options={managerOptions}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="managers"
                                  />,
                                )}
                                value={values?.dynamicArea[daIndex]?.manager}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `dynamicArea[${daIndex}].manager`,
                                    opt,
                                  )
                                }
                                isLoading={!managerOptions}
                                hasSearchBox
                                hasSelectOptions
                                error={
                                  errors?.dynamicArea &&
                                  errors?.dynamicArea[daIndex]?.manager
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppSingleSelect
                                  options={reportOptions.wasteManagement}
                                  onChange={(opt) => {
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].wasteManagement`,
                                      opt,
                                    );
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].operation`,
                                      null,
                                    );
                                  }}
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="waste.management.single"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="delete-bin-6" />}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="select.a.waste.management"
                                    />,
                                  )}
                                  value={values.dynamicArea[daIndex].wasteManagement}
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.wasteManagement
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppSingleSelect
                                  options={
                                    !values.dynamicArea[daIndex].wasteManagement
                                      ? []
                                      : values.dynamicArea[daIndex]
                                            .wasteManagement === 23
                                        ? reportOptions.eliminationOperation
                                        : reportOptions.valuationOperation
                                  }
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].operation`,
                                      opt,
                                    )
                                  }
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="operation"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="arrow-left-right" />}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="select.an.operation"
                                    />,
                                  )}
                                  value={values.dynamicArea[daIndex].operation}
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.operation
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppInputNumber
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="quantity"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="scales-3" />}
                                  value={values.dynamicArea[daIndex].quantity}
                                  onChange={(value) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].quantity`,
                                      value,
                                    )
                                  }
                                  decimals={3}
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.quantity
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppSingleSelect
                                  options={reportOptions.measureUnit}
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].measureUnit`,
                                      opt,
                                    )
                                  }
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="measure.unit"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="ruler-2" />}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="select.a.measure.unit"
                                    />,
                                  )}
                                  value={values.dynamicArea[daIndex].measureUnit}
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.measureUnit
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppInputNumber
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="valuable.material.percentage"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="percent" />}
                                  value={
                                    values.dynamicArea[daIndex].valuablePercentage
                                  }
                                  onChange={(value) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].valuablePercentage`,
                                      value,
                                    )
                                  }
                                  decimals={3}
                                  error={
                                    errors?.dynamicArea &&
                                    errors.dynamicArea[daIndex]?.valuablePercentage
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        {!values.id && (
                          <div className="row mt-3">
                            <div className="col-sm-12">
                              <span
                                className="pointer"
                                onClick={() =>
                                  arrayHelpers.push({
                                    code: null,
                                    quantity: '',
                                    measureUnit: null,
                                    wasteManagement: null,
                                    operation: null,
                                    valuablePercentage: '',
                                  })
                                }
                                ref={dynamicAreaButton}
                              >
                                <AppRemixIcon icon="add-box" />{' '}
                                <Translator
                                  file="module-comsaReports"
                                  name="add.more.types"
                                />
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  />
                  <div className="col-12">
                    <AppTextArea
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="m-cr-comment" />,
                      )}
                      value={values?.comment}
                      onChange={(v) => setFieldValue(`comment`, v)}
                      height="89px"
                    />
                  </div>
                </div>
                <div className="col-12 mt-3 mb-3">
                  <label>
                    <Translator file="module-comsaReports" name="attach.evidence" />
                  </label>
                  <AppFileUploader
                    isMulti
                    placeholder={
                      !values.files || Object.keys(values?.files).length === 0
                        ? ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="select.one.multiple.files"
                            />,
                          )
                        : ReactDOMServer.renderToString(
                            <Translator
                              file="general"
                              name="select.number.of.elements"
                              variables={{
                                elements: Object.keys(values?.files).length,
                              }}
                            />,
                          )
                    }
                    onChange={(opt) => setFieldValue('files', opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                </div>
              </div>
              {values.id && Object.keys(report?.files).length > 0 && (
                <div className="row">
                  <div className="col-12">
                    <label>
                      <Translator file="general" name="general.delete" />
                    </label>
                    <div>
                      {Object.keys(values.files).map((salt, index) => (
                        <div key={index}>
                          <label className="mb-2 pointer font-weight-light">
                            <input
                              type="checkbox"
                              className="mr-1"
                              value={salt}
                              onChange={() =>
                                handleDeletedFiles(
                                  setFieldValue,
                                  values.deletedFiles,
                                  salt,
                                )
                              }
                              checked={
                                values.deletedFiles && values.deletedFiles[salt]
                              }
                            />
                            {values.files[salt].name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
};

export default ModuleComsaWasteManagementEdit;
